// Copyright (c) StrangeBee 2024

import { useCallback, useEffect } from 'react';
import {
    QueryKey,
    UseQueryResult,
    useQuery,
    RefetchOptions,
    RefetchQueryFilters,
    QueryObserverResult,
    UseQueryOptions,
} from 'react-query';
import { ParentController, QueryParams } from 'thehive-sdk';

export interface GetQueryInterface<T> {
    fetchCallback: () => Promise<T>;
    key: QueryKey;
    manual?: boolean;
    retry?: boolean;
    fetchOnStart?: boolean;
    optionalDependencies?: any[];
}

interface UseConvertToReactQueryProps<T> {
    key: QueryKey;
    fetchCallback: () => Promise<T>;
    fetchOnStart?: boolean;
    manual?: boolean;
    retry?: boolean;
    config?: UseQueryOptions<T>;
}

export type RefetchType<T> = <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
) => Promise<QueryObserverResult<T, QueryKey>>;

interface UseGenericCountQueryParams {
    queryParams: QueryParams;
    controller: ParentController;
    fetchOnStart?: boolean;
    manual?: boolean;
}

const useListenQuery = <T, R>(
    data: any,
    manual = false,
    error: any,
    refetch: RefetchType<T>,
    query: R,
    fetchOnStart = true,
): R => {
    useEffect(() => {
        if (error) {
            return;
        }
        const canRefresh = !manual || (manual && fetchOnStart && data === undefined);

        if (canRefresh) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch, manual, fetchOnStart]);

    return query;
};

export const useGenericQuery = <T>({
    fetchOnStart,
    manual,
    key,
    fetchCallback,
    retry,
}: UseConvertToReactQueryProps<T>): UseQueryResult<T, QueryKey> => {
    if (fetchOnStart === false) {
        manual = true;
    }

    const query = useQuery<T, QueryKey, any>(key, () => fetchCallback(), {
        refetchInterval: false,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        enabled: !manual,
        retry,
    });

    const { data, refetch, error } = query;

    return useListenQuery<T, UseQueryResult<T, QueryKey>>(data, manual, error, refetch, query, fetchOnStart);
};

export const useGenericCountQuery = (
    { queryParams, controller, fetchOnStart, manual }: UseGenericCountQueryParams,
    customCount?: boolean,
): UseQueryResult<number, QueryKey> => {
    const {
        selector: { name },
        filters,
    } = queryParams;

    const fetchCallback = useCallback(
        () => (customCount ? controller.custom<number>(queryParams) : controller.count(queryParams)),
        [queryParams, controller, customCount],
    );

    const query = useQuery<number, QueryKey, any>([name, filters, 'count'], () => fetchCallback(), {
        refetchInterval: false,
        refetchOnWindowFocus: false,
        enabled: !manual,
    });

    const { refetch, data, error } = query;

    return useListenQuery<number, UseQueryResult<number, QueryKey>>(data, manual, error, refetch, query, fetchOnStart);
};
